import { API as API$6, JWTApi } from '@gratify-eu/api-client';
import { createAction, createReducer } from 'redux-act';
import { get, kebabCase, find, omitBy, findIndex, reject, reduce, keyBy, filter } from 'lodash';
import parseLinkHeader from 'parse-link-header';
import PropTypes from 'prop-types';

const selector = state => state.config;
let apiClassSettings = {
  default: API$6,
  entities: {
    'cart': JWTApi,
    'cartItem': JWTApi,
    'contact': JWTApi,
    'user': JWTApi,
    'videoPlayer': JWTApi
  }
};
const setApiClassSettings = settings => {
  apiClassSettings = { ...apiClassSettings,
    ...settings
  };
};
const getApiClass = entity => {
  if (entity && apiClassSettings.entities[entity]) {
    return apiClassSettings.entities[entity];
  }

  return apiClassSettings.default;
};

var createSymbol = function createSymbol(name) {
  return "@@redux-saga/" + name;
};

var CANCEL =
/*#__PURE__*/
createSymbol('CANCEL_PROMISE');
var IO =
/*#__PURE__*/
createSymbol('IO');
var MULTICAST =
/*#__PURE__*/
createSymbol('MULTICAST');

var undef = function undef(v) {
  return v === null || v === undefined;
};
var notUndef = function notUndef(v) {
  return v !== null && v !== undefined;
};
var func = function func(f) {
  return typeof f === 'function';
};
var string = function string(s) {
  return typeof s === 'string';
};
var array = Array.isArray;
var object = function object(obj) {
  return obj && !array(obj) && typeof obj === 'object';
};
var pattern = function pattern(pat) {
  return pat && (string(pat) || symbol(pat) || func(pat) || array(pat) && pat.every(pattern));
};
var channel = function channel(ch) {
  return ch && func(ch.take) && func(ch.close);
};
var stringableFunc = function stringableFunc(f) {
  return func(f) && f.hasOwnProperty('toString');
};
var symbol = function symbol(sym) {
  return Boolean(sym) && typeof Symbol === 'function' && sym.constructor === Symbol && sym !== Symbol.prototype;
};
var multicast = function multicast(ch) {
  return channel(ch) && ch[MULTICAST];
};
var effect = function effect(eff) {
  return eff && eff[IO];
};

function delayP(ms, val) {
  if (val === void 0) {
    val = true;
  }

  var timeoutId;
  var promise = new Promise(function (resolve) {
    timeoutId = setTimeout(resolve, ms, val);
  });

  promise[CANCEL] = function () {
    clearTimeout(timeoutId);
  };

  return promise;
}

if (process.env.NODE_ENV !== 'production' && typeof Proxy !== 'undefined') ;
var identity = function identity(v) {
  return v;
};
function check(value, predicate, error) {
  if (!predicate(value)) {
    throw new Error(error);
  }
}

var kThrow = function kThrow(err) {
  throw err;
};

var kReturn = function kReturn(value) {
  return {
    value: value,
    done: true
  };
};

function makeIterator(next, thro, name) {
  if (thro === void 0) {
    thro = kThrow;
  }

  if (name === void 0) {
    name = 'iterator';
  }

  var iterator = {
    meta: {
      name: name
    },
    next: next,
    throw: thro,
    return: kReturn,
    isSagaIterator: true
  };

  if (typeof Symbol !== 'undefined') {
    iterator[Symbol.iterator] = function () {
      return iterator;
    };
  }

  return iterator;
}

var TAKE = 'TAKE';
var PUT = 'PUT';
var ALL = 'ALL';
var CALL = 'CALL';
var FORK = 'FORK';
var SELECT = 'SELECT';

var makeEffect = function makeEffect(type, payload) {
  var _ref;

  return _ref = {}, _ref[IO] = true, _ref.combinator = false, _ref.type = type, _ref.payload = payload, _ref;
};
function take(patternOrChannel, multicastPattern) {
  if (patternOrChannel === void 0) {
    patternOrChannel = '*';
  }

  if (process.env.NODE_ENV !== 'production' && arguments.length) {
    check(arguments[0], notUndef, 'take(patternOrChannel): patternOrChannel is undefined');
  }

  if (pattern(patternOrChannel)) {
    return makeEffect(TAKE, {
      pattern: patternOrChannel
    });
  }

  if (multicast(patternOrChannel) && notUndef(multicastPattern) && pattern(multicastPattern)) {
    return makeEffect(TAKE, {
      channel: patternOrChannel,
      pattern: multicastPattern
    });
  }

  if (channel(patternOrChannel)) {
    return makeEffect(TAKE, {
      channel: patternOrChannel
    });
  }

  if (process.env.NODE_ENV !== 'production') {
    throw new Error("take(patternOrChannel): argument " + patternOrChannel + " is not valid channel or a valid pattern");
  }
}
function put(channel$1, action) {
  if (process.env.NODE_ENV !== 'production') {
    if (arguments.length > 1) {
      check(channel$1, notUndef, 'put(channel, action): argument channel is undefined');
      check(channel$1, channel, "put(channel, action): argument " + channel$1 + " is not a valid channel");
      check(action, notUndef, 'put(channel, action): argument action is undefined');
    } else {
      check(channel$1, notUndef, 'put(action): argument action is undefined');
    }
  }

  if (undef(action)) {
    action = channel$1; // `undefined` instead of `null` to make default parameter work

    channel$1 = undefined;
  }

  return makeEffect(PUT, {
    channel: channel$1,
    action: action
  });
}
function all(effects) {
  var eff = makeEffect(ALL, effects);
  eff.combinator = true;
  return eff;
}

var validateFnDescriptor = function validateFnDescriptor(effectName, fnDescriptor) {
  check(fnDescriptor, notUndef, effectName + ": argument fn is undefined or null");

  if (func(fnDescriptor)) {
    return;
  }

  var context = null;
  var fn;

  if (array(fnDescriptor)) {
    context = fnDescriptor[0];
    fn = fnDescriptor[1];
    check(fn, notUndef, effectName + ": argument of type [context, fn] has undefined or null `fn`");
  } else if (object(fnDescriptor)) {
    context = fnDescriptor.context;
    fn = fnDescriptor.fn;
    check(fn, notUndef, effectName + ": argument of type {context, fn} has undefined or null `fn`");
  } else {
    check(fnDescriptor, func, effectName + ": argument fn is not function");
    return;
  }

  if (context && string(fn)) {
    check(context[fn], func, effectName + ": context arguments has no such method - \"" + fn + "\"");
    return;
  }

  check(fn, func, effectName + ": unpacked fn argument (from [context, fn] or {context, fn}) is not a function");
};

function getFnCallDescriptor(fnDescriptor, args) {
  var context = null;
  var fn;

  if (func(fnDescriptor)) {
    fn = fnDescriptor;
  } else {
    if (array(fnDescriptor)) {
      context = fnDescriptor[0];
      fn = fnDescriptor[1];
    } else {
      context = fnDescriptor.context;
      fn = fnDescriptor.fn;
    }

    if (context && string(fn) && func(context[fn])) {
      fn = context[fn];
    }
  }

  return {
    context: context,
    fn: fn,
    args: args
  };
}

var isNotDelayEffect = function isNotDelayEffect(fn) {
  return fn !== delay;
};

function call(fnDescriptor) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  if (process.env.NODE_ENV !== 'production') {
    var arg0 = typeof args[0] === 'number' ? args[0] : 'ms';
    check(fnDescriptor, isNotDelayEffect, "instead of writing `yield call(delay, " + arg0 + ")` where delay is an effect from `redux-saga/effects` you should write `yield delay(" + arg0 + ")`");
    validateFnDescriptor('call', fnDescriptor);
  }

  return makeEffect(CALL, getFnCallDescriptor(fnDescriptor, args));
}
function apply(context, fn, args) {
  if (args === void 0) {
    args = [];
  }

  var fnDescriptor = [context, fn];

  if (process.env.NODE_ENV !== 'production') {
    validateFnDescriptor('apply', fnDescriptor);
  }

  return makeEffect(CALL, getFnCallDescriptor([context, fn], args));
}
function fork(fnDescriptor) {
  if (process.env.NODE_ENV !== 'production') {
    validateFnDescriptor('fork', fnDescriptor);
    check(fnDescriptor, function (arg) {
      return !effect(arg);
    }, 'fork: argument must not be an effect');
  }

  for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    args[_key3 - 1] = arguments[_key3];
  }

  return makeEffect(FORK, getFnCallDescriptor(fnDescriptor, args));
}
function select(selector) {
  if (selector === void 0) {
    selector = identity;
  }

  for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
    args[_key5 - 1] = arguments[_key5];
  }

  if (process.env.NODE_ENV !== 'production' && arguments.length) {
    check(arguments[0], notUndef, 'select(selector, [...]): argument selector is undefined');
    check(selector, func, "select(selector, [...]): argument " + selector + " is not a function");
  }

  return makeEffect(SELECT, {
    selector: selector,
    args: args
  });
}
var delay =
/*#__PURE__*/
call.bind(null, delayP);

var done = function done(value) {
  return {
    done: true,
    value: value
  };
};

var qEnd = {};
function safeName(patternOrChannel) {
  if (channel(patternOrChannel)) {
    return 'channel';
  }

  if (stringableFunc(patternOrChannel)) {
    return String(patternOrChannel);
  }

  if (func(patternOrChannel)) {
    return patternOrChannel.name;
  }

  return String(patternOrChannel);
}
function fsmIterator(fsm, startState, name) {
  var stateUpdater,
      errorState,
      effect,
      nextState = startState;

  function next(arg, error) {
    if (nextState === qEnd) {
      return done(arg);
    }

    if (error && !errorState) {
      nextState = qEnd;
      throw error;
    } else {
      stateUpdater && stateUpdater(arg);
      var currentState = error ? fsm[errorState](error) : fsm[nextState]();
      nextState = currentState.nextState;
      effect = currentState.effect;
      stateUpdater = currentState.stateUpdater;
      errorState = currentState.errorState;
      return nextState === qEnd ? done(arg) : effect;
    }
  }

  return makeIterator(next, function (error) {
    return next(null, error);
  }, name);
}

function takeEvery(patternOrChannel, worker) {
  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  var yTake = {
    done: false,
    value: take(patternOrChannel)
  };

  var yFork = function yFork(ac) {
    return {
      done: false,
      value: fork.apply(void 0, [worker].concat(args, [ac]))
    };
  };

  var action,
      setAction = function setAction(ac) {
    return action = ac;
  };

  return fsmIterator({
    q1: function q1() {
      return {
        nextState: 'q2',
        effect: yTake,
        stateUpdater: setAction
      };
    },
    q2: function q2() {
      return {
        nextState: 'q1',
        effect: yFork(action)
      };
    }
  }, 'q1', "takeEvery(" + safeName(patternOrChannel) + ", " + worker.name + ")");
}

var validateTakeEffect = function validateTakeEffect(fn, patternOrChannel, worker) {
  check(patternOrChannel, notUndef, fn.name + " requires a pattern or channel");
  check(worker, notUndef, fn.name + " requires a saga parameter");
};

function takeEvery$1(patternOrChannel, worker) {
  if (process.env.NODE_ENV !== 'production') {
    validateTakeEffect(takeEvery$1, patternOrChannel, worker);
  }

  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  return fork.apply(void 0, [takeEvery, patternOrChannel, worker].concat(args));
}

const serverErrorAction = createAction('server error', text => ({
  text
}));
const unauthorizedErrorAction = createAction('unauthorized error', response => ({
  response
}));
function listFetchActions(entity) {
  return {
    request: createAction(`fetch ${entity} list`, (params, url = null) => ({
      params,
      url
    })),
    failure: createAction(`${entity} fetch error`, (message, response) => ({
      message,
      response
    })),
    success: createAction(`receive ${entity} list`, (entityList, response) => ({
      [`${entity}List`]: entityList,
      response
    }), (entityList, response) => {
      let meta = {};
      const linkHeader = get(response, 'headers.link');
      const resultCount = parseInt(get(response, 'headers.x-result-count'), 10);

      if (linkHeader) {
        meta.pagination = parseLinkHeader(linkHeader);
      }

      if (resultCount) {
        meta.resultCount = resultCount;
      }

      return meta;
    })
  };
}
function entityFetchActions(entity) {
  return {
    request: createAction(`fetch ${entity} entity`, (id, params, url = null) => ({
      id,
      params,
      url
    })),
    failure: createAction(`${entity} entity fetch error`, (message, response) => ({
      message,
      response
    })),
    success: createAction(`receive ${entity} entity`, (entityItem, response) => ({
      [entity]: entityItem,
      response
    }))
  };
}
function entityOptionsActions(entity) {
  return {
    request: createAction(`fetch ${entity} options`, (id = null, params = null, url = null) => ({
      id,
      params,
      url
    })),
    failure: createAction(`${entity} options fetch error`, (message, response) => ({
      message,
      response
    })),
    success: createAction(`receive ${entity} options`, (entityItem, id = null, response) => ({
      [entity]: entityItem,
      id,
      response
    }))
  };
}
function entityUpdateActions(entity) {
  return {
    request: createAction(`update ${entity} entity`, (id, data) => ({
      id,
      data
    })),
    failure: createAction(`${entity} entity update error`, (message, response) => ({
      message,
      response
    })),
    success: createAction(`receive updated ${entity} entity`, (entityItem, response) => ({
      [entity]: entityItem,
      response
    }))
  };
}
function entityCreateActions(entity) {
  return {
    request: createAction(`create ${entity} entity`, data => ({
      data
    })),
    failure: createAction(`${entity} entity create error`, (message, response) => ({
      message,
      response
    })),
    success: createAction(`receive created ${entity} entity`, (entityItem, response) => ({
      [entity]: entityItem,
      response
    }))
  };
}
function entityDeleteActions(entity) {
  return {
    request: createAction(`delete ${entity} entity`, (id, entityItem, url = null) => ({
      id,
      entityItem,
      url
    })),
    failure: createAction(`${entity} entity delete error`, (message, response) => ({
      message,
      response
    })),
    success: createAction(`receive deleted ${entity} entity`, (id, entityItem, url = null, response) => ({
      id,
      [entity]: entityItem,
      url,
      response
    }))
  };
}
function* handleError(e, actions) {
  if (e.response) {
    const statusCode = get(e.response, 'status', null);

    if (statusCode === 401) {
      yield put(unauthorizedErrorAction(e.response));
    }

    if (statusCode >= 500) {
      yield put(serverErrorAction(e.response.statusText));
    }

    yield put(actions.failure(e.response.data, e.response));
  } else {
    console.error('error ', e);
  }
}
function fetchListWorker(route, actions, API, configSelector) {
  return function* fetchEntityList(action, throwError = false, returnResponse = false) {
    try {
      const {
        url,
        params
      } = get(action, 'payload', {});
      const config = yield select(configSelector);
      const api = new API(config);
      let response;

      if (url) {
        response = yield apply(api, api.call, [url, 'GET', null, params]);
      } else {
        response = yield apply(api, api['fetchList'], [route, params]);
      }

      yield put(actions.success(response.data, response));
      return returnResponse ? response : response.data;
    } catch (e) {
      yield call(handleError, e, actions);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  };
}
function fetchEntityWorker(route, actions, API, configSelector) {
  return function* fetchEntity(action, throwError = false) {
    try {
      const {
        id,
        url,
        params
      } = get(action, 'payload', {});
      const config = yield select(configSelector);
      const api = new API(config);
      let response;

      if (url) {
        response = yield apply(api, api.call, [url, 'GET', null, params]);
      } else {
        response = yield apply(api, api[`fetchEntity`], [route, id, params]);
      }

      yield put(actions.success(response.data, response));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  };
}
function fetchOptionsWorker(route, actions, API, configSelector) {
  return function* fetchEntity(action) {
    try {
      const {
        id,
        url,
        params
      } = get(action, 'payload', {});
      const config = yield select(configSelector);
      const api = new API(config);
      let response;

      if (url) {
        response = yield apply(api, api.call, [url, 'OPTIONS', null, params]);
      } else if (id) {
        response = yield apply(api, api[`fetchOptions`], [route, id, params]);
      } else {
        response = yield apply(api, api[`fetchRouteOptions`], [route, params]);
      }

      yield put(actions.success(response.data, id, response));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);
      return e;
    }
  };
}
function updateEntityWorker(route, actions, API, configSelector) {
  return function* updateEntity(action, throwError = false) {
    try {
      const {
        id,
        data
      } = get(action, 'payload', {});
      const config = yield select(configSelector);
      const api = new API(config);
      const response = yield apply(api, api[`updateEntity`], [route, id, data]);
      yield put(actions.success(response.data, response));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  };
}
function createEntityWorker(route, actions, API, configSelector) {
  return function* createEntity(action, throwError = false) {
    try {
      const {
        data
      } = get(action, 'payload', {});
      const config = yield select(configSelector);
      const api = new API(config);
      const response = yield apply(api, api[`createEntity`], [route, data]);
      yield put(actions.success(response.data, response));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  };
}
function deleteEntityWorker(route, actions, API, configSelector) {
  return function* deleteEntity(action, throwError = false) {
    try {
      const {
        id,
        data,
        entityItem,
        url
      } = get(action, 'payload', {});
      const config = yield select(configSelector);
      const api = new API(config);
      let response;

      if (url) {
        response = yield apply(api, api.call, [url, 'DELETE']);
      } else {
        response = yield apply(api, api[`deleteEntity`], [route, id, data]);
      }

      yield put(actions.success(id, entityItem, url, response));
    } catch (e) {
      yield call(handleError, e, actions);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  };
}
class Entity {
  constructor(entityName, ApiClass, configSelector, {
    route,
    key = 'url'
  } = {}) {
    this.entity = entityName;
    let routeName = route ? route : `${kebabCase(entityName)}s`;
    const {
      entity
    } = this;
    this.actions = {
      fetchList: listFetchActions(entity),
      fetch: entityFetchActions(entity),
      update: entityUpdateActions(entity),
      create: entityCreateActions(entity),
      del: entityDeleteActions(entity),
      options: entityOptionsActions(entity)
    };
    const {
      fetchList,
      fetch,
      update,
      create,
      del,
      options
    } = this.actions;
    this.workers = {
      fetchList: fetchListWorker(routeName, fetchList, ApiClass, configSelector),
      fetch: fetchEntityWorker(routeName, fetch, ApiClass, configSelector),
      update: updateEntityWorker(routeName, update, ApiClass, configSelector),
      create: createEntityWorker(routeName, create, ApiClass, configSelector),
      del: deleteEntityWorker(routeName, del, ApiClass, configSelector),
      options: fetchOptionsWorker(routeName, options, ApiClass, configSelector)
    };
    this.saga = [takeEvery$1(fetchList.request().type, this.workers.fetchList), takeEvery$1(fetch.request().type, this.workers.fetch), takeEvery$1(update.request().type, this.workers.update), takeEvery$1(create.request().type, this.workers.create), takeEvery$1(del.request().type, this.workers.del), takeEvery$1(options.request().type, this.workers.options)];
    this.reducer = {
      [fetchList.success]: (state, payload) => {
        if (payload[`${entity}List`]) {
          let newState = Object.assign({}, state);
          payload[`${entity}List`].forEach(object => newState[object[key]] = object);
          return newState;
        }

        return state;
      },
      [fetch.success]: (state, payload) => Object.assign({}, state, {
        [payload[entity][key]]: payload[entity]
      }),
      [update.success]: (state, payload) => Object.assign({}, state, {
        [payload[entity][key]]: payload[entity]
      }),
      [create.success]: (state, payload) => Object.assign({}, state, {
        [payload[entity][key]]: payload[entity]
      }),
      [del.success]: (state, payload) => {
        const {
          id,
          url
        } = payload || {};

        if (id) {
          const deleted = find(state, {
            id: payload.id
          });

          if (deleted) {
            return omitBy(state, item => {
              //console.log(item.id, payload.id);
              return item[key] === deleted[key];
            });
          }
        } else if (url) {
          let newState = Object.assign({}, state);
          delete newState[url];
          return newState;
        }

        return state;
      }
    };
  }

}

const entityName = 'application';
const entity = new Entity(entityName, API$6, selector, {
  key: 'id'
}); // ACTIONS

const actions = entity.actions; // WORKERS

const workers = { ...entity.workers
}; // SAGA

function* saga() {
  yield all(entity.saga);
}
const reducer = createReducer(entity.reducer, {});
var applications = {
  actions,
  workers,
  saga,
  reducer
};

var applications$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions,
  workers: workers,
  saga: saga,
  reducer: reducer,
  'default': applications
});

const entityName$1 = 'cartItem';
const API = getApiClass(entityName$1);
const entity$1 = new Entity(entityName$1, API, selector); // ACTIONS

const actions$1 = entity$1.actions; // WORKERS

const workers$1 = { ...entity$1.workers
}; // SAGA

function* saga$1() {
  yield all(entity$1.saga);
}
const reducer$1 = createReducer(entity$1.reducer, {});
var cartItems = {
  actions: actions$1,
  workers: workers$1,
  saga: saga$1,
  reducer: reducer$1
};

var cartItems$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$1,
  workers: workers$1,
  saga: saga$1,
  reducer: reducer$1,
  'default': cartItems
});

const entityName$2 = 'cart';
const API$1 = getApiClass(entityName$2);
const entity$2 = new Entity(entityName$2, API$1, selector);

function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

const shouldUpdate = (oldDate, newDate) => {
  if (oldDate && newDate) {
    let d1 = new Date(oldDate);
    let d2 = new Date(newDate);

    if (isValidDate(d1) && isValidDate(d2)) {
      return d1.getTime() < d2.getTime();
    }
  }

  return true;
}; // ACTIONS


const actions$2 = { ...entity$2.actions,
  cartAction: {
    request: createAction('cart action request', actionUrl => ({
      actionUrl
    })),
    failure: createAction('cart action failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('cart action success', cart => ({
      cart
    }))
  },
  shippingPrices: {
    request: createAction('shipping prices request', cart => ({
      cart
    })),
    failure: createAction('shipping prices failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('shipping prices success', (cart, prices) => ({
      cart,
      prices
    }))
  },
  applyCode: {
    request: createAction('apply discount code request', (cart, code) => ({
      cart,
      code
    })),
    failure: createAction('apply discount code failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('apply discount code success', cart => ({
      cart
    }))
  },
  removeCode: {
    request: createAction('remove discount code request', (cart, code) => ({
      cart,
      code
    })),
    failure: createAction('remove discount code failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('remove discount code success', cart => ({
      cart
    }))
  }
}; // WORKERS

const workers$2 = { ...entity$2.workers,

  *applyCartAction(action, throwError = false) {
    const {
      actionUrl,
      params
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$1(config);
      const response = yield apply(api, api.cartAction, [actionUrl, params]);
      yield put(actions$2.cartAction.success(response.data));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$2.cartAction);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  },

  *shippingPricesWorker(action, throwError = false) {
    const {
      cart
    } = get(action, 'payload', {});

    try {
      if (cart && cart.shipping_prices) {
        const config = yield select(selector);
        const api = new API$1(config);
        const response = yield apply(api, api.call, [cart.shipping_prices, 'GET']);
        yield put(actions$2.shippingPrices.success(cart, response.data));
        return response.data;
      }
    } catch (e) {
      yield call(handleError, e, actions$2.shippingPrices);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  },

  *applyCode(action, throwError = false) {
    const {
      cart,
      code
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$1(config);
      const response = yield apply(api, api.cartAction, [`${cart.url}applycode/`, {
        code
      }]);
      yield put(actions$2.applyCode.success(response.data));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$2.applyCode);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  },

  *removeCode(action, throwError = false) {
    const {
      cart,
      code
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$1(config);
      const response = yield apply(api, api.cartAction, [`${cart.url}removecode/`, {
        code
      }]);
      yield put(actions$2.removeCode.success(response.data));
      return response.data;
    } catch (e) {
      console.log(e);
      yield call(handleError, e, actions$2.removeCode);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  }

}; // SAGA

function* saga$2() {
  yield all([...entity$2.saga, takeEvery$1(actions$2.cartAction.request().type, workers$2.applyCartAction), takeEvery$1(actions$2.shippingPrices.request().type, workers$2.shippingPricesWorker), takeEvery$1(actions$2.applyCode.request().type, workers$2.applyCode), takeEvery$1(actions$2.removeCode.request().type, workers$2.removeCode)]);
}
const reducer$2 = createReducer({ ...entity$2.reducer,
  [actions$1.update.success]: (state, payload) => {
    const {
      cartItem
    } = payload;
    let newState = Object.assign({}, state);
    const activeCart = get(newState, cartItem.cart, null);

    if (activeCart) {
      let newCart = Object.assign({}, activeCart);
      const itemIndex = findIndex(activeCart.contents, {
        url: cartItem.url
      });

      if (itemIndex > -1) {
        const oldCartItem = newCart.contents[itemIndex];

        if (oldCartItem && shouldUpdate(oldCartItem.updated_at, cartItem.updated_at)) {
          const newContents = newCart.contents.slice();
          newContents[itemIndex] = cartItem;
          newCart.contents = newContents;
          newState[newCart.url] = newCart;
          return newState;
        }
      }
    }

    return state;
  },
  [actions$1.create.success]: (state, payload) => {
    const {
      cartItem
    } = payload;
    let newState = Object.assign({}, state);
    const activeCart = get(newState, cartItem.cart, null);

    if (activeCart) {
      let newCart = Object.assign({}, activeCart);
      const newContents = newCart.contents.slice();
      newContents.push(cartItem);
      newCart.contents = newContents;
      newState[newCart.url] = newCart;
      return newState;
    }

    return state;
  },
  [actions$1.del.success]: (state, payload) => {
    const {
      id,
      cartItem
    } = payload;
    console.log('payload', payload);
    let newState = Object.assign({}, state);
    const activeCart = get(newState, cartItem.cart, null);

    if (activeCart) {
      let newCart = Object.assign({}, activeCart);
      newCart.contents = reject(newCart.contents, {
        url: cartItem.url
      });
      newState[newCart.url] = newCart;
      return newState;
    }

    return state;
  },
  [actions$2.cartAction.success]: (state, payload) => {
    const {
      cart
    } = payload;
    return Object.assign({}, state, {
      [cart.url]: cart
    });
  },
  [actions$2.applyCode.success]: (state, payload) => {
    const {
      cart
    } = payload;
    return Object.assign({}, state, {
      [cart.url]: cart
    });
  },
  [actions$2.removeCode.success]: (state, payload) => {
    const {
      cart
    } = payload;
    return Object.assign({}, state, {
      [cart.url]: cart
    });
  }
}, {});
var carts = {
  actions: actions$2,
  workers: workers$2,
  saga: saga$2,
  reducer: reducer$2
};

var carts$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$2,
  workers: workers$2,
  saga: saga$2,
  reducer: reducer$2,
  'default': carts
});

const entityName$3 = 'checkoutSettings';
const entity$3 = new Entity(entityName$3, API$6, selector, {
  key: 'url',
  route: 'checkout-settings'
}); // ACTIONS

const actions$3 = entity$3.actions; // WORKERS

const workers$3 = { ...entity$3.workers
}; // SAGA

function* saga$3() {
  yield all(entity$3.saga);
}
const reducer$3 = createReducer(entity$3.reducer, {});
var checkoutSettings = {
  actions: actions$3,
  workers: workers$3,
  saga: saga$3,
  reducer: reducer$3
};

var checkoutSettings$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$3,
  workers: workers$3,
  saga: saga$3,
  reducer: reducer$3,
  'default': checkoutSettings
});

const entityName$4 = 'contact';
const API$2 = getApiClass(entityName$4);
const entity$4 = new Entity(entityName$4, API$2, selector); // ACTIONS

const actions$4 = entity$4.actions; // WORKERS

const workers$4 = { ...entity$4.workers
}; // SAGA

function* saga$4() {
  yield all(entity$4.saga);
}
const reducer$4 = createReducer(entity$4.reducer, {});
var contacts = {
  actions: actions$4,
  workers: workers$4,
  saga: saga$4,
  reducer: reducer$4
};

var contacts$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$4,
  workers: workers$4,
  saga: saga$4,
  reducer: reducer$4,
  'default': contacts
});

const entityName$5 = 'downloadAccess';
const entity$5 = new Entity(entityName$5, API$6, selector, {
  key: 'url',
  route: 'download-accesses'
}); // ACTIONS

const actions$5 = { ...entity$5.actions,
  downloadAction: {
    request: createAction('download action request', actionUrl => ({
      actionUrl
    })),
    failure: createAction('download action failure', message => ({
      message
    })),
    success: createAction('download action success', download => ({
      download
    }))
  }
}; // WORKERS

const workers$5 = { ...entity$5.workers,

  *applyDownloadAction(action) {
    const {
      actionUrl,
      params
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const response = yield apply(api, api.call, [actionUrl, 'POST']);
      yield put(actions$5.downloadAction.success(response.data));
    } catch (e) {
      handleError(e, actions$5.downloadAction);
      throw e;
    }
  }

}; // SAGA

function* saga$5() {
  yield all([...entity$5.saga, takeEvery$1(actions$5.downloadAction.request().type, workers$5.applyDownloadAction)]);
}
const reducer$5 = createReducer(entity$5.reducer, {});
var downloadAccesses = {
  actions: actions$5,
  workers: workers$5,
  saga: saga$5,
  reducer: reducer$5
};

var downloadAccesses$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$5,
  workers: workers$5,
  saga: saga$5,
  reducer: reducer$5,
  'default': downloadAccesses
});

const entityName$6 = 'download';
const entity$6 = new Entity(entityName$6, API$6, selector); // ACTIONS

const actions$6 = entity$6.actions; // WORKERS

const workers$6 = { ...entity$6.workers
}; // SAGA

function* saga$6() {
  yield all(entity$6.saga);
}
const reducer$6 = createReducer(entity$6.reducer, {});
const STATE_ENABLED = 'enabled';
const STATE_DISABLED = 'disabled';
const STATE_DELETED = 'deleted';
const states = {
  STATE_ENABLED,
  STATE_DISABLED,
  STATE_DELETED
};
var downloads = {
  actions: actions$6,
  workers: workers$6,
  saga: saga$6,
  reducer: reducer$6
};

var downloads$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$6,
  workers: workers$6,
  saga: saga$6,
  reducer: reducer$6,
  STATE_ENABLED: STATE_ENABLED,
  STATE_DISABLED: STATE_DISABLED,
  STATE_DELETED: STATE_DELETED,
  states: states,
  'default': downloads
});

const entityName$7 = 'fileUpload';
const entity$7 = new Entity(entityName$7, API$6, selector); // ACTIONS

const actions$7 = { ...entity$7.actions,
  uploadFile: {
    request: createAction('upload file request', file => ({
      file
    })),
    failure: createAction('upload file failed', message => ({
      message
    })),
    success: createAction('upload file succeeded', data => ({
      data
    }))
  }
}; // WORKERS

const workers$7 = { ...entity$7.workers,

  *uploadFile(action) {
    const {
      file,
      progressHandler = () => {}
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      let data = new FormData();
      data.append('file', file);
      const response = yield apply(api, api.uploadFile, [data, progressHandler]); //console.log(response.data);

      yield put(actions$7.uploadFile.success(response.data));
      return response.data;
    } catch (e) {
      handleError(e, actions$7.uploadFile);
      throw e;
    }
  }

}; // SAGA

function* saga$7() {
  yield all([...entity$7.saga, takeEvery$1(actions$7.uploadFile.request().type, workers$7.uploadFile)]);
}
const reducer$7 = createReducer({ ...entity$7.reducer,
  [actions$7.uploadFile.success]: (state, payload) => Object.assign({}, state, {
    [payload['data']['url']]: payload['data']
  })
}, {});
var fileUploads = {
  actions: actions$7,
  workers: workers$7,
  saga: saga$7,
  reducer: reducer$7
};

var fileUploads$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$7,
  workers: workers$7,
  saga: saga$7,
  reducer: reducer$7,
  'default': fileUploads
});

const entityName$8 = 'image';
const entity$8 = new Entity(entityName$8, API$6, selector); // ACTIONS

const actions$8 = entity$8.actions; // WORKERS

const workers$8 = { ...entity$8.workers
}; // SAGA

function* saga$8() {
  yield all(entity$8.saga);
}
const reducer$8 = createReducer(entity$8.reducer, {});
var images = {
  actions: actions$8,
  workers: workers$8,
  saga: saga$8,
  reducer: reducer$8
};

var images$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$8,
  workers: workers$8,
  saga: saga$8,
  reducer: reducer$8,
  'default': images
});

const __ = val => {
  return val;
};

const entityName$9 = 'order';
const entity$9 = new Entity(entityName$9, API$6, selector); // ACTIONS

const actions$9 = { ...entity$9.actions,
  orderAction: {
    request: createAction('order action request', actionUrl => ({
      actionUrl
    })),
    failure: createAction('order action failure', message => ({
      message
    })),
    success: createAction('order action success', order => ({
      order
    }))
  },
  orderExport: {
    request: createAction('order export request', (filters, format) => ({
      filters,
      format
    })),
    failure: createAction('order export failure', message => ({
      message
    })),
    success: createAction('order export success', order => ({
      order
    }))
  }
}; // WORKERS

const workers$9 = { ...entity$9.workers,

  *applyOrderAction(action) {
    const {
      actionUrl,
      params
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const response = yield apply(api, api.orderAction, [actionUrl, params]);
      yield put(actions$9.orderAction.success(response.data));
    } catch (e) {
      handleError(e, actions$9.orderAction);
      throw e;
    }
  },

  *exportOrders(action) {
    const {
      filters,
      format,
      shopId
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const response = yield apply(api, api.orderExport, [filters, format, shopId]);
      yield put(actions$9.orderExport.success(response));
      return response;
    } catch (e) {
      handleError(e, actions$9.orderExport);
      throw e;
    }
  }

}; // SAGA

function* saga$9() {
  yield all([...entity$9.saga, takeEvery$1(actions$9.orderAction.request().type, workers$9.applyOrderAction)]);
}
const STATUS_PENDING = 'pending';
const STATUS_RECEIVED = 'received';
const STATUS_SHIPPED = 'shipped';
const STATUS_COMPLETED = 'completed';
const STATUS_CANCELLED = 'cancelled';
const getStatusOptions = () => [{
  value: STATUS_PENDING,
  label: __('Pending'),
  text: __('Pending')
}, {
  value: STATUS_RECEIVED,
  label: __('Received'),
  text: __('Received')
}, {
  value: STATUS_SHIPPED,
  label: __('Shipped'),
  text: __('Shipped')
}, {
  value: STATUS_COMPLETED,
  label: __('Completed'),
  text: __('Completed')
}, {
  value: STATUS_CANCELLED,
  label: __('Cancelled'),
  text: __('Cancelled')
}];
const PAYMENT_STATUS_PENDING = 'pending';
const PAYMENT_STATUS_PAID = 'paid';
const PAYMENT_STATUS_FAILED = 'failed';
const PAYMENT_STATUS_AUTHORIZED = 'authorized';
const getPaymentStatusOptions = () => [{
  value: PAYMENT_STATUS_PENDING,
  label: __('Pending'),
  text: __('Pending')
}, {
  value: PAYMENT_STATUS_PAID,
  label: __('Paid'),
  text: __('Paid')
}, {
  value: PAYMENT_STATUS_FAILED,
  label: __('Payment Failed'),
  text: __('Payment Failed')
}, {
  value: PAYMENT_STATUS_AUTHORIZED,
  label: __('Authorized'),
  text: __('Authorized')
}];
const ACTION_RECEIVE = 'receive';
const ACTION_PAY = 'pay';
const ACTION_SHIP = 'ship';
const ACTION_COMPLETE = 'complete';
const ACTION_CANCEL = 'cancel';
const ACTION_FAIL_PAYMENT = 'failpayment';
const ACTION_RESEND_INVOICE = 'resendinvoice';
const getOrderStatusActions = () => [{
  value: ACTION_RECEIVE,
  label: __('Set Order Received'),
  text: __('Set Order Received')
}, {
  value: ACTION_PAY,
  label: __("Set Order Paid"),
  text: __("Set Order Paid")
}, {
  value: ACTION_SHIP,
  label: __("Set Order Shipped"),
  text: __("Set Order Shipped")
}, {
  value: ACTION_COMPLETE,
  label: __("Complete Order"),
  text: __("Complete Order")
}, {
  value: ACTION_CANCEL,
  label: __("Cancel Order"),
  text: __("Cancel Order")
}, {
  value: ACTION_FAIL_PAYMENT,
  label: __("Set Order Payment Failed"),
  text: __("Set Order Payment Failed")
}];
const getOrderActions = () => {
  const statusActions = getOrderStatusActions();
  return [...statusActions, {
    value: ACTION_RESEND_INVOICE,
    label: __("Re-send invoice"),
    text: __("Re-send invoice")
  }];
};
const reducer$9 = createReducer({ ...entity$9.reducer,
  [actions$9.orderAction.success]: (state, payload) => Object.assign({}, state, {
    [payload[entityName$9]['url']]: payload[entityName$9]
  })
}, {});
var orders = {
  actions: actions$9,
  workers: workers$9,
  saga: saga$9,
  reducer: reducer$9
};

var orders$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$9,
  workers: workers$9,
  saga: saga$9,
  STATUS_PENDING: STATUS_PENDING,
  STATUS_RECEIVED: STATUS_RECEIVED,
  STATUS_SHIPPED: STATUS_SHIPPED,
  STATUS_COMPLETED: STATUS_COMPLETED,
  STATUS_CANCELLED: STATUS_CANCELLED,
  getStatusOptions: getStatusOptions,
  PAYMENT_STATUS_PENDING: PAYMENT_STATUS_PENDING,
  PAYMENT_STATUS_PAID: PAYMENT_STATUS_PAID,
  PAYMENT_STATUS_FAILED: PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_AUTHORIZED: PAYMENT_STATUS_AUTHORIZED,
  getPaymentStatusOptions: getPaymentStatusOptions,
  ACTION_RECEIVE: ACTION_RECEIVE,
  ACTION_PAY: ACTION_PAY,
  ACTION_SHIP: ACTION_SHIP,
  ACTION_COMPLETE: ACTION_COMPLETE,
  ACTION_CANCEL: ACTION_CANCEL,
  ACTION_FAIL_PAYMENT: ACTION_FAIL_PAYMENT,
  ACTION_RESEND_INVOICE: ACTION_RESEND_INVOICE,
  getOrderStatusActions: getOrderStatusActions,
  getOrderActions: getOrderActions,
  reducer: reducer$9,
  'default': orders
});

const actions$a = {
  fetchList: {
    request: createAction('fetch order chart items', (shop, params) => ({
      shop,
      params
    })),
    failure: createAction('fetch order chart items failure', (message, response) => ({
      message,
      response
    })),
    success: createAction('fetch order chart items success', (shop, chartItems, response) => ({
      shop,
      chartItems,
      response
    }))
  }
}; // WORKERS

const workers$a = {
  *fetchListWorker(action, throwError = false) {
    const {
      shop,
      params
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      let requestParams = { ...params,
        shop
      };
      const response = yield apply(api, api.callRoute, ['orders/chart/', 'GET', null, requestParams]);
      yield put(actions$a.fetchList.success(shop, response.data, response));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$a.fetchList);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  }

}; // SAGA

function* saga$a() {
  yield all([takeEvery$1(actions$a.fetchList.request().type, workers$a.fetchListWorker)]);
}
const reducer$a = createReducer({
  [actions$a.fetchList.success]: (state, payload) => {
    if (payload['chartItems'] && payload['shop']) {
      const {
        chartItems,
        shop
      } = payload;
      let newState = Object.assign({}, state);
      newState[shop] = chartItems;
      return newState;
    }

    return state;
  }
}, {});
var orderChartItems = {
  actions: actions$a,
  workers: workers$a,
  saga: saga$a,
  reducer: reducer$a
};

var orderChartItems$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$a,
  workers: workers$a,
  saga: saga$a,
  reducer: reducer$a,
  'default': orderChartItems
});

const entityName$a = 'orderHistoryEvent';
const entity$a = new Entity(entityName$a, API$6, selector); // ACTIONS

const actions$b = entity$a.actions; // WORKERS

const workers$b = { ...entity$a.workers
}; // SAGA

function* saga$b() {
  yield all(entity$a.saga);
}
const reducer$b = createReducer(entity$a.reducer, {});
var orderHistoryEvents = {
  actions: actions$b,
  workers: workers$b,
  saga: saga$b,
  reducer: reducer$b
};

var orderHistoryEvents$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$b,
  workers: workers$b,
  saga: saga$b,
  reducer: reducer$b,
  'default': orderHistoryEvents
});

const entityName$b = 'orderItem';
const entity$b = new Entity(entityName$b, API$6, selector); // ACTIONS

const actions$c = entity$b.actions; // WORKERS

const workers$c = { ...entity$b.workers
}; // SAGA

function* saga$c() {
  yield all(entity$b.saga);
}
const reducer$c = createReducer(entity$b.reducer, {});
var orderItems = {
  actions: actions$c,
  workers: workers$c,
  saga: saga$c,
  reducer: reducer$c
};

var orderItems$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$c,
  workers: workers$c,
  saga: saga$c,
  reducer: reducer$c,
  'default': orderItems
});

const actions$d = {
  fetch: {
    request: createAction('fetch order overview', (shop, params) => ({
      shop,
      params
    })),
    failure: createAction('fetch order overview failure', (message, response) => ({
      message,
      response
    })),
    success: createAction('fetch order overview success', (shop, overview, response) => ({
      shop,
      overview,
      response
    }))
  }
}; // WORKERS

const workers$d = {
  *fetchWorker(action, throwError = false) {
    const {
      shop,
      params
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      let requestParams = { ...params,
        shop
      };
      const response = yield apply(api, api.callRoute, ['orders/overview/', 'GET', null, requestParams]);
      yield put(actions$d.fetch.success(shop, response.data, response));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$d.fetch);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  }

}; // SAGA

function* saga$d() {
  yield all([takeEvery$1(actions$d.fetch.request().type, workers$d.fetchWorker)]);
}
const reducer$d = createReducer({
  [actions$d.fetch.success]: (state, payload) => {
    if (payload['overview'] && payload['shop']) {
      const {
        overview,
        shop
      } = payload;
      let newState = Object.assign({}, state);
      newState[shop] = overview;
      return newState;
    }

    return state;
  }
}, {});
var orderOverviews = {
  actions: actions$d,
  workers: workers$d,
  saga: saga$d,
  reducer: reducer$d
};

var orderOverviews$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$d,
  workers: workers$d,
  saga: saga$d,
  reducer: reducer$d,
  'default': orderOverviews
});

const entityName$c = 'orderShippingPrice';
const entity$c = new Entity(entityName$c, API$6, selector); // ACTIONS

const actions$e = entity$c.actions; // WORKERS

const workers$e = { ...entity$c.workers
}; // SAGA

function* saga$e() {
  yield all(entity$c.saga);
}
const reducer$e = createReducer(entity$c.reducer, {});
var orderShippingPrices = {
  actions: actions$e,
  workers: workers$e,
  saga: saga$e,
  reducer: reducer$e
};

var orderShippingPrices$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$e,
  workers: workers$e,
  saga: saga$e,
  reducer: reducer$e,
  'default': orderShippingPrices
});

const entityName$d = 'paymentMethod';
const entity$d = new Entity(entityName$d, API$6, selector); // ACTIONS

const actions$f = entity$d.actions; // WORKERS

const workers$f = { ...entity$d.workers
}; // SAGA

function* saga$f() {
  yield all(entity$d.saga);
}
const reducer$f = createReducer(entity$d.reducer, {});
var paymentMethods = {
  actions: actions$f,
  workers: workers$f,
  saga: saga$f,
  reducer: reducer$f
};

var paymentMethods$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$f,
  workers: workers$f,
  saga: saga$f,
  reducer: reducer$f,
  'default': paymentMethods
});

const entityName$e = 'pricingCategory';
const entity$e = new Entity(entityName$e, API$6, selector, {
  route: 'shipping-pricing-categories',
  key: 'url'
}); // ACTIONS

const actions$g = entity$e.actions; // WORKERS

const workers$g = { ...entity$e.workers
}; // SAGA

function* saga$g() {
  yield all(entity$e.saga);
}
const reducer$g = createReducer(entity$e.reducer, {});
var pricingCategories = {
  actions: actions$g,
  workers: workers$g,
  saga: saga$g,
  reducer: reducer$g
};

var pricingCategories$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$g,
  workers: workers$g,
  saga: saga$g,
  reducer: reducer$g,
  'default': pricingCategories
});

const entityName$f = 'pricingCategoryPrice';
const entity$f = new Entity(entityName$f, API$6, selector); // ACTIONS

const actions$h = entity$f.actions; // WORKERS

const workers$h = { ...entity$f.workers
}; // SAGA

function* saga$h() {
  yield all(entity$f.saga);
}
const reducer$h = createReducer(entity$f.reducer, {});
var pricingCategoryPrices = {
  actions: actions$h,
  workers: workers$h,
  saga: saga$h,
  reducer: reducer$h
};

var pricingCategoryPrices$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$h,
  workers: workers$h,
  saga: saga$h,
  reducer: reducer$h,
  'default': pricingCategoryPrices
});

const entityName$g = 'product';
const entity$g = new Entity(entityName$g, API$6, selector); // ACTIONS

const actions$i = entity$g.actions; // WORKERS

const workers$i = { ...entity$g.workers
}; // SAGA

function* saga$i() {
  yield all(entity$g.saga);
}
const reducer$i = createReducer(entity$g.reducer, {});
const STATE_ACTIVE = 'active';
const STATE_HIDDEN = 'hidden';
const STATE_DELETED$1 = 'deleted';
const states$1 = {
  STATE_ACTIVE,
  STATE_HIDDEN,
  STATE_DELETED: STATE_DELETED$1
};
const getStateChoices = () => {
  return [{
    value: STATE_ACTIVE,
    label: 'Active',
    text: 'Active'
  }, {
    value: STATE_HIDDEN,
    label: 'Hidden',
    text: 'Hidden'
  }];
};
var products = {
  actions: actions$i,
  workers: workers$i,
  saga: saga$i,
  reducer: reducer$i
};

var products$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$i,
  workers: workers$i,
  saga: saga$i,
  reducer: reducer$i,
  STATE_ACTIVE: STATE_ACTIVE,
  STATE_HIDDEN: STATE_HIDDEN,
  STATE_DELETED: STATE_DELETED$1,
  states: states$1,
  getStateChoices: getStateChoices,
  'default': products
});

const entityName$h = 'productImage';
const entity$h = new Entity(entityName$h, API$6, selector); // ACTIONS

const actions$j = entity$h.actions; // WORKERS

const workers$j = { ...entity$h.workers
}; // SAGA

function* saga$j() {
  yield all(entity$h.saga);
}
const reducer$j = createReducer(entity$h.reducer, {});
var productImages = {
  actions: actions$j,
  workers: workers$j,
  saga: saga$j,
  reducer: reducer$j
};

var productImages$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$j,
  workers: workers$j,
  saga: saga$j,
  reducer: reducer$j,
  'default': productImages
});

const entityName$i = 'productImageManager';
const entity$i = new Entity(entityName$i, API$6, selector); // ACTIONS

const actions$k = entity$i.actions; // WORKERS

const workers$k = { ...entity$i.workers
}; // SAGA

function* saga$k() {
  yield all(entity$i.saga);
}
const reducer$k = createReducer(entity$i.reducer, {});
const STATE_ENABLED$1 = 'enabled';
const STATE_DISABLED$1 = 'disabled';
const STATE_DELETED$2 = 'deleted';
const states$2 = {
  STATE_ENABLED: STATE_ENABLED$1,
  STATE_DISABLED: STATE_DISABLED$1,
  STATE_DELETED: STATE_DELETED$2
};
var productImageManagers = {
  actions: actions$k,
  workers: workers$k,
  saga: saga$k,
  reducer: reducer$k
};

var productImageManagers$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$k,
  workers: workers$k,
  saga: saga$k,
  reducer: reducer$k,
  STATE_ENABLED: STATE_ENABLED$1,
  STATE_DISABLED: STATE_DISABLED$1,
  STATE_DELETED: STATE_DELETED$2,
  states: states$2,
  'default': productImageManagers
});

const entityName$j = 'productVideo';
const entity$j = new Entity(entityName$j, API$6, selector); // ACTIONS

const actions$l = entity$j.actions; // WORKERS

const workers$l = { ...entity$j.workers
}; // SAGA

function* saga$l() {
  yield all(entity$j.saga);
}
const reducer$l = createReducer(entity$j.reducer, {});
const AVAILABILITY_PAID = 'paid';
const AVAILABILITY_FREE_FOR_EVERYONE = 'free_for_everyone';
const availability = {
  AVAILABILITY_PAID,
  AVAILABILITY_FREE_FOR_EVERYONE
};
const getAvailabilityChoices = () => [{
  value: AVAILABILITY_PAID,
  label: __("Paid")
}, {
  value: AVAILABILITY_FREE_FOR_EVERYONE,
  label: __("Free for Everyone")
}];
var productVideos = {
  actions: actions$l,
  workers: workers$l,
  saga: saga$l,
  reducer: reducer$l
};

var productVideos$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$l,
  workers: workers$l,
  saga: saga$l,
  reducer: reducer$l,
  AVAILABILITY_PAID: AVAILABILITY_PAID,
  AVAILABILITY_FREE_FOR_EVERYONE: AVAILABILITY_FREE_FOR_EVERYONE,
  availability: availability,
  getAvailabilityChoices: getAvailabilityChoices,
  'default': productVideos
});

const entityName$k = 'productVideoManager';
const entity$k = new Entity(entityName$k, API$6, selector); // ACTIONS

const actions$m = { ...entity$k.actions,
  sort: {
    request: createAction('sort videos request', (id, videos) => ({
      id,
      videos
    })),
    failure: createAction('sort videos failed', message => ({
      message
    })),
    success: createAction('sort videos success', (productVideoManager, response) => ({
      productVideoManager,
      response
    }))
  }
}; // WORKERS

const workers$m = { ...entity$k.workers,

  *sort(action) {
    const {
      id,
      videos
    } = action.payload || {};

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const route = `product-video-managers/${id}/sort/`;
      const response = yield apply(api, api.callRoute, [route, 'POST', {
        videos
      }]);
      yield put(actions$m.sort.success(response.data, response));
      return response.data;
    } catch (e) {
      handleError(e, actions$m.sort);
      throw e;
    }
  }

}; // SAGA

function* saga$m() {
  yield all([...entity$k.saga, takeEvery$1(actions$m.sort.request().type, workers$m.sort)]);
}
const reducer$m = createReducer({ ...entity$k.reducer,
  [actions$l.update.success]: (_, payload) => {
    console.log('success');
    let state = _;
    const {
      productVideo
    } = payload || {};

    if (productVideo && productVideo.url) {
      const manager = _[productVideo.manager];

      if (manager) {
        let updatedVideos = reduce(manager.videos, (videos, video) => {
          if (video.url === productVideo.url) {
            videos.push(productVideo);
          } else {
            videos.push(video);
          }

          return videos;
        }, []);
        state = { ..._,
          [manager.url]: { ...manager,
            videos: updatedVideos
          }
        };
      }
    }

    return state;
  },
  [actions$m.sort.success]: (state, payload) => Object.assign({}, state, {
    [payload[entityName$k]['url']]: payload[entityName$k]
  })
}, {});
const STATE_ENABLED$2 = 'enabled';
const STATE_DISABLED$2 = 'disabled';
const STATE_DELETED$3 = 'deleted';
const states$3 = {
  STATE_ENABLED: STATE_ENABLED$2,
  STATE_DISABLED: STATE_DISABLED$2,
  STATE_DELETED: STATE_DELETED$3
};
var productVideoManagers = {
  actions: actions$m,
  workers: workers$m,
  saga: saga$m,
  reducer: reducer$m
};

var productVideoManagers$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$m,
  workers: workers$m,
  saga: saga$m,
  reducer: reducer$m,
  STATE_ENABLED: STATE_ENABLED$2,
  STATE_DISABLED: STATE_DISABLED$2,
  STATE_DELETED: STATE_DELETED$3,
  states: states$3,
  'default': productVideoManagers
});

const entityName$l = 'productQuantity';
const entity$l = new Entity(entityName$l, API$6, selector, {
  route: 'product-quantities',
  key: 'url'
}); // ACTIONS

const actions$n = entity$l.actions; // WORKERS

const workers$n = { ...entity$l.workers
}; // SAGA

function* saga$n() {
  yield all(entity$l.saga);
}
const reducer$n = createReducer(entity$l.reducer, {});
var productQuantities = {
  actions: actions$n,
  workers: workers$n,
  saga: saga$n,
  reducer: reducer$n
};

var productQuantities$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$n,
  workers: workers$n,
  saga: saga$n,
  reducer: reducer$n,
  'default': productQuantities
});

const entityName$m = 'productShipping';
const entity$m = new Entity(entityName$m, API$6, selector); // ACTIONS

const actions$o = entity$m.actions; // WORKERS

const workers$o = { ...entity$m.workers
}; // SAGA

function* saga$o() {
  yield all(entity$m.saga);
}
const reducer$o = createReducer(entity$m.reducer, {});
const STATE_ENABLED$3 = 'enabled';
const STATE_DISABLED$3 = 'disabled';
const STATE_DELETED$4 = 'deleted';
const states$4 = {
  STATE_ENABLED: STATE_ENABLED$3,
  STATE_DISABLED: STATE_DISABLED$3,
  STATE_DELETED: STATE_DELETED$4
};
var productShippings = {
  actions: actions$o,
  workers: workers$o,
  saga: saga$o,
  reducer: reducer$o
};

var productShippings$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$o,
  workers: workers$o,
  saga: saga$o,
  reducer: reducer$o,
  STATE_ENABLED: STATE_ENABLED$3,
  STATE_DISABLED: STATE_DISABLED$3,
  STATE_DELETED: STATE_DELETED$4,
  states: states$4,
  'default': productShippings
});

const entityName$n = 'shippingMethod';
const entity$n = new Entity(entityName$n, API$6, selector); // ACTIONS

const actions$p = entity$n.actions; // WORKERS

const workers$p = { ...entity$n.workers
}; // SAGA

function* saga$p() {
  yield all(entity$n.saga);
}
const reducer$p = createReducer(entity$n.reducer, {});
var shippingMethods = {
  actions: actions$p,
  workers: workers$p,
  saga: saga$p,
  reducer: reducer$p
};

var shippingMethods$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$p,
  workers: workers$p,
  saga: saga$p,
  reducer: reducer$p,
  'default': shippingMethods
});

const entityName$o = 'shop';
const entity$o = new Entity(entityName$o, API$6, selector); // ACTIONS

const actions$q = entity$o.actions; // WORKERS

const workers$q = { ...entity$o.workers
}; // SAGA

function* saga$q() {
  yield all(entity$o.saga);
}
/*
 export const PAYMENT_STATUS_PENDING = 'pending';
 export const PAYMENT_STATUS_PAID = 'paid';
 export const PAYMENT_STATUS_FAILED = 'failed';
 export const PAYMENT_STATUS_AUTHORIZED = 'authorized';

 export const getPaymentStatusOptions = () => ([
 { value: PAYMENT_STATUS_PENDING, label: 'Pending', text: 'Pending'  },
 { value: PAYMENT_STATUS_PAID, label: 'Paid', text: 'Paid' },
 { value: PAYMENT_STATUS_FAILED, label: 'Payment Failed', text: 'Payment Failed' },
 { value: PAYMENT_STATUS_AUTHORIZED, label: 'Authorized', text: 'Authorized' },
 ]);

 */

const ACCOUNTS_DISABLED = 'disabled';
const ACCOUNTS_REQUIRED = 'required';
const ACCOUNTS_OPTIONAL = 'optional';
const getAccountOptions = () => [{
  value: ACCOUNTS_DISABLED,
  label: __("Accounts disabled"),
  text: __("Accounts disabled")
}, {
  value: ACCOUNTS_REQUIRED,
  label: __("Accounts required"),
  text: __("Accounts required")
}, {
  value: ACCOUNTS_OPTIONAL,
  label: __("Accounts optional"),
  text: __("Accounts optional")
}];
const NAME_NOT_REQUIRED = 'not_required';
const NAME_LAST_NAME_ONLY = 'last_name_only';
const NAME_FULL_NAME = 'full_name';
const getNameOptions = () => [{
  value: NAME_NOT_REQUIRED,
  label: __("Name not required"),
  text: __("Name not required")
}, {
  value: NAME_LAST_NAME_ONLY,
  label: __("Only last name required"),
  text: __("Only last name required")
}, {
  value: NAME_FULL_NAME,
  label: __("Full name required"),
  text: __("Full name required")
}];
const BILLING_ADDRESS_DISABLED = 'disabled';
const BILLING_ADDRESS_COMPANY_ONLY = 'company_only';
const BILLING_ADDRESS_ENABLED = 'enabled';
const getBillingAddressOptions = () => [{
  value: BILLING_ADDRESS_DISABLED,
  label: __("Billing Address Disabled"),
  text: __("Billing Address Disabled")
}, {
  value: BILLING_ADDRESS_COMPANY_ONLY,
  label: __("Billing Address for Companies Only"),
  text: __("Billing Address for Companies Only")
}, {
  value: BILLING_ADDRESS_ENABLED,
  label: __("Billing Address Enabled"),
  text: __("Billing Address Enabled")
}];
const ADDRESS_LINE_2_HIDDEN = 'hidden';
const ADDRESS_LINE_2_OPTIONAL = 'optional';
const ADDRESS_LINE_2_REQUIRED = 'required';
const getAddressLineOptions = () => [{
  value: ADDRESS_LINE_2_HIDDEN,
  label: __("Address Line 2 hidden"),
  text: __("Address Line 2 hidden")
}, {
  value: ADDRESS_LINE_2_OPTIONAL,
  label: __("Address Line 2 optional"),
  text: __("Address Line 2 optional")
}, {
  value: ADDRESS_LINE_2_REQUIRED,
  label: __("Address Line 2 required"),
  text: __("Address Line 2 required")
}];
const PHONE_HIDDEN = 'hidden';
const PHONE_OPTIONAL = 'optional';
const PHONE_REQUIRED = 'required';
const getPhoneOptions = () => [{
  value: PHONE_HIDDEN,
  label: __("Phone Number hidden"),
  text: __("Phone Number hidden")
}, {
  value: PHONE_OPTIONAL,
  label: __("Phone Number optional"),
  text: __("Phone Number optional")
}, {
  value: PHONE_REQUIRED,
  label: __("Phone Number required"),
  text: __("Phone Number required")
}];
const reducer$q = createReducer(entity$o.reducer, {});
var shops = {
  actions: actions$q,
  workers: workers$q,
  saga: saga$q,
  reducer: reducer$q
};

var shops$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$q,
  workers: workers$q,
  saga: saga$q,
  ACCOUNTS_DISABLED: ACCOUNTS_DISABLED,
  ACCOUNTS_REQUIRED: ACCOUNTS_REQUIRED,
  ACCOUNTS_OPTIONAL: ACCOUNTS_OPTIONAL,
  getAccountOptions: getAccountOptions,
  NAME_NOT_REQUIRED: NAME_NOT_REQUIRED,
  NAME_LAST_NAME_ONLY: NAME_LAST_NAME_ONLY,
  NAME_FULL_NAME: NAME_FULL_NAME,
  getNameOptions: getNameOptions,
  BILLING_ADDRESS_DISABLED: BILLING_ADDRESS_DISABLED,
  BILLING_ADDRESS_COMPANY_ONLY: BILLING_ADDRESS_COMPANY_ONLY,
  BILLING_ADDRESS_ENABLED: BILLING_ADDRESS_ENABLED,
  getBillingAddressOptions: getBillingAddressOptions,
  ADDRESS_LINE_2_HIDDEN: ADDRESS_LINE_2_HIDDEN,
  ADDRESS_LINE_2_OPTIONAL: ADDRESS_LINE_2_OPTIONAL,
  ADDRESS_LINE_2_REQUIRED: ADDRESS_LINE_2_REQUIRED,
  getAddressLineOptions: getAddressLineOptions,
  PHONE_HIDDEN: PHONE_HIDDEN,
  PHONE_OPTIONAL: PHONE_OPTIONAL,
  PHONE_REQUIRED: PHONE_REQUIRED,
  getPhoneOptions: getPhoneOptions,
  reducer: reducer$q,
  'default': shops
});

const entityName$p = 'shopFeed';
const entity$p = new Entity(entityName$p, API$6, selector); // ACTIONS

const actions$r = entity$p.actions; // WORKERS

const workers$r = { ...entity$p.workers
}; // SAGA

function* saga$r() {
  yield all(entity$p.saga);
}
const reducer$r = createReducer(entity$p.reducer, {});
var shopFeeds = {
  actions: actions$r,
  workers: workers$r,
  saga: saga$r,
  reducer: reducer$r
};

var shopFeeds$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$r,
  workers: workers$r,
  saga: saga$r,
  reducer: reducer$r,
  'default': shopFeeds
});

const entityName$q = 'shopPaymentMethod';
const entity$q = new Entity(entityName$q, API$6, selector); // ACTIONS

const actions$s = { ...entity$q.actions,
  paymentMethodAction: {
    request: createAction('payment method action request', (actionUrl, params) => ({
      actionUrl,
      params
    })),
    failure: createAction('payment method action failure', message => ({
      message
    })),
    success: createAction('payment method action success', data => ({
      data
    }))
  }
};
const TYPE_ROUTES = {
  'BankDepositMethod': 'shop-bank-deposit',
  'MaksekeskusMethod': 'shop-maksekeskus'
}; // WORKERS

const workers$s = { ...entity$q.workers,

  *createShopPaymentMethod(action) {
    const {
      route,
      data
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const response = yield apply(api, api.callRoute, [`${route}/`, 'POST', data]);
      yield put(actions$s.create.success(response.data));
      return response.data;
    } catch (e) {
      handleError(e, actions$s.create);
      throw e;
    }
  },

  *applyPaymentMethodAction(action, throwError = false) {
    const {
      actionUrl,
      params
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const response = yield apply(api, api.paymentMethodAction, [actionUrl, 'GET', params]); //console.log(response.data);

      yield put(actions$s.paymentMethodAction.success(response.data));
      return response.data;
    } catch (e) {
      //console.log(e);
      yield put(actions$s.paymentMethodAction.failure(e.data || e));

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  }

}; // SAGA

function* saga$s() {
  yield all([...entity$q.saga, takeEvery$1(actions$s.paymentMethodAction.request().type, workers$s.applyPaymentMethodAction)]);
}
const reducer$s = createReducer(entity$q.reducer, {});
var shopPaymentMethods = {
  actions: actions$s,
  workers: workers$s,
  saga: saga$s,
  reducer: reducer$s
};

var shopPaymentMethods$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$s,
  TYPE_ROUTES: TYPE_ROUTES,
  workers: workers$s,
  saga: saga$s,
  reducer: reducer$s,
  'default': shopPaymentMethods
});

const entityName$r = 'shopRole';
const entity$r = new Entity(entityName$r, API$6, selector); // ACTIONS

const actions$t = entity$r.actions; // WORKERS

const workers$t = { ...entity$r.workers
}; // SAGA

function* saga$t() {
  yield all(entity$r.saga);
}
const reducer$t = createReducer(entity$r.reducer, {});
var shopRoles = {
  actions: actions$t,
  workers: workers$t,
  saga: saga$t,
  reducer: reducer$t
};

var shopRoles$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$t,
  workers: workers$t,
  saga: saga$t,
  reducer: reducer$t,
  'default': shopRoles
});

const entityName$s = 'shopSearch';
const entity$s = new Entity(entityName$s, API$6, selector, {
  key: 'url',
  route: 'shop-search'
}); // ACTIONS

const actions$u = entity$s.actions; // WORKERS

const workers$u = { ...entity$s.workers
}; // SAGA

function* saga$u() {
  yield all(entity$s.saga);
}
const reducer$u = createReducer(entity$s.reducer, {});
var shopSearches = {
  actions: actions$u,
  workers: workers$u,
  saga: saga$u,
  reducer: reducer$u
};

var shopSearches$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$u,
  workers: workers$u,
  saga: saga$u,
  reducer: reducer$u,
  'default': shopSearches
});

const entityName$t = 'shopShippingMethod';
const entity$t = new Entity(entityName$t, API$6, selector); // ACTIONS

const actions$v = entity$t.actions;
const TYPE_ROUTES$1 = {
  'SmartPostParcelMethod': 'shop-smartpost-parcel',
  'OmnivaParcelMethod': 'shop-omniva-parcel',
  'DPDParcelMethod': 'shop-dpd-parcel',
  'FlatFeeMethod': 'shop-flat-fee-methods',
  'CustomMethod': 'shop-custom-methods'
}; // WORKERS

const workers$v = { ...entity$t.workers,

  *createShopShippingMethod(action) {
    const {
      route,
      data
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const response = yield apply(api, api.callRoute, [`${route}/`, 'POST', data]);
      yield put(actions$v.create.success(response.data));
      return response.data;
    } catch (e) {
      handleError(e, actions$v.create);
      throw e;
    }
  }

}; // SAGA

function* saga$v() {
  yield all(entity$t.saga);
}
const reducer$v = createReducer(entity$t.reducer, {});
var shopShippingMethods = {
  actions: actions$v,
  workers: workers$v,
  saga: saga$v,
  reducer: reducer$v
};

var shopShippingMethods$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$v,
  TYPE_ROUTES: TYPE_ROUTES$1,
  workers: workers$v,
  saga: saga$v,
  reducer: reducer$v,
  'default': shopShippingMethods
});

const entityName$u = 'shopVat';
const entity$u = new Entity(entityName$u, API$6, selector); // ACTIONS

const actions$w = entity$u.actions; // WORKERS

const workers$w = { ...entity$u.workers
}; // SAGA

function* saga$w() {
  yield all(entity$u.saga);
}
const reducer$w = createReducer(entity$u.reducer, {});
var shopVats = {
  actions: actions$w,
  workers: workers$w,
  saga: saga$w,
  reducer: reducer$w
};

var shopVats$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$w,
  workers: workers$w,
  saga: saga$w,
  reducer: reducer$w,
  'default': shopVats
});

const entityName$v = 'ticket';
const entity$v = new Entity(entityName$v, API$6, selector); // ACTIONS

const actions$x = entity$v.actions; // WORKERS

const workers$x = { ...entity$v.workers
}; // SAGA

function* saga$x() {
  yield all(entity$v.saga);
}
const reducer$x = createReducer(entity$v.reducer, {});
const getTypes = () => {
  return [{
    value: 'single_ticket',
    label: 'Single Ticket'
  }, {
    value: 'multiple_tickets',
    label: 'Multiple Tickets'
  }];
};
const STATE_ACTIVE$1 = 'active';
const STATE_DISABLED$4 = 'disabled';
const STATE_DELETED$5 = 'deleted';
const states$5 = {
  STATE_ACTIVE: STATE_ACTIVE$1,
  STATE_DISABLED: STATE_DISABLED$4,
  STATE_DELETED: STATE_DELETED$5
};
var tickets = {
  actions: actions$x,
  workers: workers$x,
  saga: saga$x,
  reducer: reducer$x
};

var tickets$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$x,
  workers: workers$x,
  saga: saga$x,
  reducer: reducer$x,
  getTypes: getTypes,
  STATE_ACTIVE: STATE_ACTIVE$1,
  STATE_DISABLED: STATE_DISABLED$4,
  STATE_DELETED: STATE_DELETED$5,
  states: states$5,
  'default': tickets
});

const entityName$w = 'ticketPurchase';
const entity$w = new Entity(entityName$w, API$6, selector); // ACTIONS

const actions$y = entity$w.actions; // WORKERS

const workers$y = { ...entity$w.workers
}; // SAGA

function* saga$y() {
  yield all(entity$w.saga);
}
const reducer$y = createReducer(entity$w.reducer, {});
var ticketPurchases = {
  actions: actions$y,
  workers: workers$y,
  saga: saga$y,
  reducer: reducer$y
};

var ticketPurchases$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$y,
  workers: workers$y,
  saga: saga$y,
  reducer: reducer$y,
  'default': ticketPurchases
});

const entityName$x = 'ticketPurchaseRegistration';
const entity$x = new Entity(entityName$x, API$6, selector); // ACTIONS

const actions$z = entity$x.actions; // WORKERS

const workers$z = { ...entity$x.workers
}; // SAGA

function* saga$z() {
  yield all(entity$x.saga);
}
const reducer$z = createReducer(entity$x.reducer, {});
var ticketPurchaseRegistrations = {
  actions: actions$z,
  workers: workers$z,
  saga: saga$z,
  reducer: reducer$z
};

var ticketPurchaseRegistrations$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$z,
  workers: workers$z,
  saga: saga$z,
  reducer: reducer$z,
  'default': ticketPurchaseRegistrations
});

const entityName$y = 'user';
const API$3 = getApiClass(entityName$y);
const entity$y = new Entity(entityName$y, API$3, selector); // ACTIONS

const actions$A = { ...entity$y.actions,
  fetchCurrentUser: {
    request: createAction('fetch current user'),
    failure: createAction('fetch current user failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('fetch current user success', user => ({
      user
    }))
  }
}; // WORKERS

const workers$A = { ...entity$y.workers,

  *fetchCurrentUser(action, throwError = false) {
    try {
      const config = yield select(selector);
      const api = new API$3(config);
      const response = yield apply(api, api.callRoute, ['users/current_user/', 'GET']);
      yield put(actions$A.fetchCurrentUser.success(response.data));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$A.fetchCurrentUser);

      if (throwError) {
        throw e;
      } else {
        return e;
      }
    }
  }

}; // SAGA

function* saga$A() {
  yield all([...entity$y.saga, takeEvery$1(actions$A.fetchCurrentUser.request().type, workers$A.fetchCurrentUser)]);
}
const reducer$A = createReducer({
  [actions$A.fetchCurrentUser.success]: (state, payload) => {
    const {
      user
    } = payload || {};

    if (user) {
      return { ...state,
        [user.url]: user
      };
    }

    return state;
  },
  ...entity$y.reducer
}, {});
var users = {
  actions: actions$A,
  workers: workers$A,
  saga: saga$A,
  reducer: reducer$A
};

var users$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$A,
  workers: workers$A,
  saga: saga$A,
  reducer: reducer$A,
  'default': users
});

const entityName$z = 'discount';
const entity$z = new Entity(entityName$z, API$6, selector); // ACTIONS

const actions$B = { ...entity$z.actions,
  enable: {
    request: createAction('enable discount request', id => ({
      id
    })),
    failure: createAction('enable discount failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('enable discount success', discount => ({
      discount
    }))
  },
  disable: {
    request: createAction('disable discount request', id => ({
      id
    })),
    failure: createAction('disable discount failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('disable discount success', discount => ({
      discount
    }))
  }
}; // WORKERS

const workers$B = { ...entity$z.workers,

  *enableDiscount(action, throwError = true) {
    const {
      id
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const route = `discounts/${id}/enable/`;
      const response = yield apply(api, api.callRoute, [route, 'POST']);
      yield put(actions$B.enable.success(response.data));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$B.enable);

      if (throwError) {
        throw e;
      }
    }
  },

  *disableDiscount(action, throwError = true) {
    const {
      id
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const route = `discounts/${id}/disable/`;
      const response = yield apply(api, api.callRoute, [route, 'POST']);
      yield put(actions$B.enable.success(response.data));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$B.enable);

      if (throwError) {
        throw e;
      }
    }
  }

}; // SAGA

function* saga$B() {
  yield all([...entity$z.saga, takeEvery$1(actions$B.enable.request().type, workers$B.enableDiscount), takeEvery$1(actions$B.disable.request().type, workers$B.disableDiscount)]);
}
const reducer$B = createReducer({ ...entity$z.reducer,
  [actions$B.enable.success]: (_, payload) => Object.assign({}, _, {
    [payload['discount']['url']]: payload['discount']
  }),
  [actions$B.disable.success]: (_, payload) => Object.assign({}, _, {
    [payload['discount']['url']]: payload['discount']
  })
}, {});
var discounts = {
  actions: actions$B,
  workers: workers$B,
  saga: saga$B,
  reducer: reducer$B
};

var discounts$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$B,
  workers: workers$B,
  saga: saga$B,
  reducer: reducer$B,
  'default': discounts
});

const entityName$A = 'discountProduct';
const entity$A = new Entity(entityName$A, API$6, selector); // ACTIONS

const actions$C = entity$A.actions; // WORKERS

const workers$C = { ...entity$A.workers
}; // SAGA

function* saga$C() {
  yield all(entity$A.saga);
}
const reducer$C = createReducer(entity$A.reducer, {});
var discountProducts = {
  actions: actions$C,
  workers: workers$C,
  saga: saga$C,
  reducer: reducer$C
};

var discountProducts$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$C,
  workers: workers$C,
  saga: saga$C,
  reducer: reducer$C,
  'default': discountProducts
});

const entityName$B = 'orderDiscount';
const entity$B = new Entity(entityName$B, API$6, selector); // ACTIONS

const actions$D = entity$B.actions; // WORKERS

const workers$D = { ...entity$B.workers
}; // SAGA

function* saga$D() {
  yield all(entity$B.saga);
}
const reducer$D = createReducer(entity$B.reducer, {});
var orderDiscounts = {
  actions: actions$D,
  workers: workers$D,
  saga: saga$D,
  reducer: reducer$D
};

var orderDiscounts$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$D,
  workers: workers$D,
  saga: saga$D,
  reducer: reducer$D,
  'default': orderDiscounts
});

const entityName$C = 'productTag';
const entity$C = new Entity(entityName$C, API$6, selector); // ACTIONS

const actions$E = entity$C.actions; // WORKERS

const workers$E = { ...entity$C.workers
}; // SAGA

function* saga$E() {
  yield all(entity$C.saga);
}
const reducer$E = createReducer(entity$C.reducer, {});
var productTags = {
  actions: actions$E,
  workers: workers$E,
  saga: saga$E,
  reducer: reducer$E
};

var productTags$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$E,
  workers: workers$E,
  saga: saga$E,
  reducer: reducer$E,
  'default': productTags
});

const entityName$D = 'productType';
const entity$D = new Entity(entityName$D, API$6, selector); // ACTIONS

const actions$F = entity$D.actions; // WORKERS

const workers$F = { ...entity$D.workers
}; // SAGA

function* saga$F() {
  yield all(entity$D.saga);
}
const reducer$F = createReducer(entity$D.reducer, {});
var productTypes = {
  actions: actions$F,
  workers: workers$F,
  saga: saga$F,
  reducer: reducer$F
};

var productTypes$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$F,
  workers: workers$F,
  saga: saga$F,
  reducer: reducer$F,
  'default': productTypes
});

const entityName$E = 'productVendor';
const entity$E = new Entity(entityName$E, API$6, selector); // ACTIONS

const actions$G = entity$E.actions; // WORKERS

const workers$G = { ...entity$E.workers
}; // SAGA

function* saga$G() {
  yield all(entity$E.saga);
}
const reducer$G = createReducer(entity$E.reducer, {});
var productVendors = {
  actions: actions$G,
  workers: workers$G,
  saga: saga$G,
  reducer: reducer$G
};

var productVendors$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$G,
  workers: workers$G,
  saga: saga$G,
  reducer: reducer$G,
  'default': productVendors
});

const entityName$F = 'collection';
const entity$F = new Entity(entityName$F, API$6, selector); // ACTIONS

const actions$H = { ...entity$F.actions,
  fetchProducts: {
    request: createAction('fetch products request', id => ({
      id
    })),
    failure: createAction('fetch products failure', (message, error) => ({
      message,
      error
    })),
    success: createAction('fetch products  success', products => ({
      products
    }))
  }
}; // WORKERS

const workers$H = { ...entity$F.workers,

  *fetchProducts(action, throwError = true) {
    const {
      id
    } = get(action, 'payload', {});

    try {
      const config = yield select(selector);
      const api = new API$6(config);
      const route = `collections/${id}/products/`;
      const response = yield apply(api, api.callRoute, [route, 'GET']);
      yield put(actions$H.fetchProducts.success(response.data));
      return response.data;
    } catch (e) {
      yield call(handleError, e, actions$H.fetchProducts);

      if (throwError) {
        throw e;
      }
    }
  }

}; // SAGA

function* saga$H() {
  yield all([...entity$F.saga, takeEvery$1(actions$H.fetchProducts.request().type, workers$H.fetchProducts)]);
}
const reducer$H = createReducer(entity$F.reducer, {});
var collections = {
  actions: actions$H,
  workers: workers$H,
  saga: saga$H,
  reducer: reducer$H
};

var collections$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$H,
  workers: workers$H,
  saga: saga$H,
  reducer: reducer$H,
  'default': collections
});

const entityName$G = 'collectionCondition';
const entity$G = new Entity(entityName$G, API$6, selector); // ACTIONS

const actions$I = entity$G.actions; // WORKERS

const workers$I = { ...entity$G.workers
}; // SAGA

function* saga$I() {
  yield all(entity$G.saga);
}
const reducer$I = createReducer(entity$G.reducer, {});
var collectionConditions = {
  actions: actions$I,
  workers: workers$I,
  saga: saga$I,
  reducer: reducer$I
};

var collectionConditions$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$I,
  workers: workers$I,
  saga: saga$I,
  reducer: reducer$I,
  'default': collectionConditions
});

const entityName$H = 'collectionProduct';
const entity$H = new Entity(entityName$H, API$6, selector); // ACTIONS

const actions$J = entity$H.actions; // WORKERS

const workers$J = { ...entity$H.workers
}; // SAGA

function* saga$J() {
  yield all(entity$H.saga);
}
const reducer$J = createReducer(entity$H.reducer, {});
var collectionProducts = {
  actions: actions$J,
  workers: workers$J,
  saga: saga$J,
  reducer: reducer$J
};

var collectionProducts$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$J,
  workers: workers$J,
  saga: saga$J,
  reducer: reducer$J,
  'default': collectionProducts
});

const route = 'products/analytics';
const entityName$I = 'product';
const entity$I = new Entity(entityName$I, API$6, selector, {
  route,
  key: 'url'
}); // ACTIONS

const actions$K = entity$I.actions; // WORKERS

const workers$K = { ...entity$I.workers
}; // SAGA

function* saga$K() {
  yield all(entity$I.saga);
}
const reducer$K = createReducer(entity$I.reducer, {});
var productAnalytics = {
  actions: actions$K,
  workers: workers$K,
  saga: saga$K,
  reducer: reducer$K
};

var productAnalytics$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$K,
  workers: workers$K,
  saga: saga$K,
  reducer: reducer$K,
  'default': productAnalytics
});

const entityName$J = 'video';
const entity$J = new Entity(entityName$J, API$6, selector); // ACTIONS

const actions$L = entity$J.actions; // WORKERS

const workers$L = { ...entity$J.workers
}; // SAGA

function* saga$L() {
  yield all(entity$J.saga);
}
const reducer$L = createReducer(entity$J.reducer, {});
var videos = {
  actions: actions$L,
  workers: workers$L,
  saga: saga$L,
  reducer: reducer$L
};

var videos$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$L,
  workers: workers$L,
  saga: saga$L,
  reducer: reducer$L,
  'default': videos
});

const entityName$K = 'productAccess';
const entity$K = new Entity(entityName$K, API$6, selector, {
  route: 'product-accesses',
  key: 'url'
}); // ACTIONS

const actions$M = entity$K.actions; // WORKERS

const workers$M = { ...entity$K.workers
}; // SAGA

function* saga$M() {
  yield all(entity$K.saga);
}
const reducer$M = createReducer(entity$K.reducer, {});
var productAccesses = {
  actions: actions$M,
  workers: workers$M,
  saga: saga$M,
  reducer: reducer$M
};

var productAccesses$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$M,
  workers: workers$M,
  saga: saga$M,
  reducer: reducer$M,
  'default': productAccesses
});

const entityName$L = 'videoPlayer';
const API$4 = getApiClass(entityName$L);
const entity$L = new Entity(entityName$L, API$4, selector); // ACTIONS

const actions$N = entity$L.actions; // WORKERS

const workers$N = { ...entity$L.workers
}; // SAGA

function* saga$N() {
  yield all(entity$L.saga);
}
const reducer$N = createReducer(entity$L.reducer, {});
var videoPlayers = {
  actions: actions$N,
  workers: workers$N,
  saga: saga$N,
  reducer: reducer$N
};

var videoPlayers$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$N,
  workers: workers$N,
  saga: saga$N,
  reducer: reducer$N,
  'default': videoPlayers
});

const reducer$O = createReducer({
  [actions$2.shippingPrices.success]: (state, payload) => {
    const {
      cart,
      prices
    } = payload;
    return Object.assign({}, state, {
      [cart.url]: prices
    });
  }
}, {});
var shippingPrices = {
  reducer: reducer$O
};

var shippingPrices$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  reducer: reducer$O,
  'default': shippingPrices
});

const entityName$M = 'videoAccess';
const API$5 = getApiClass(entityName$M);
const entity$M = new Entity(entityName$M, API$5, selector, {
  key: 'key',
  route: 'video-access'
}); // ACTIONS

const actions$O = entity$M.actions; // WORKERS

const workers$O = { ...entity$M.workers
}; // SAGA

function* saga$O() {
  yield all(entity$M.saga);
}
const reducer$P = createReducer(entity$M.reducer, {});
var videoAccesses = {
  actions: actions$O,
  workers: workers$O,
  saga: saga$O,
  reducer: reducer$P
};

var videoAccesses$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  actions: actions$O,
  workers: workers$O,
  saga: saga$O,
  reducer: reducer$P,
  'default': videoAccesses
});

function* saga$P() {
  yield all([fork(saga), fork(saga$1), fork(saga$2), fork(saga$3), fork(saga$H), fork(saga$I), fork(saga$J), fork(saga$4), fork(saga$5), fork(saga$6), fork(saga$7), fork(saga$8), fork(saga$9), fork(saga$a), fork(saga$b), fork(saga$c), fork(saga$d), fork(saga$e), fork(saga$f), fork(saga$g), fork(saga$h), fork(saga$i), fork(saga$K), fork(saga$j), fork(saga$k), fork(saga$l), fork(saga$m), fork(saga$n), fork(saga$o), fork(saga$E), fork(saga$F), fork(saga$G), fork(saga$q), fork(saga$r), // fork(shopFeedItems.saga),
  fork(saga$s), fork(saga$t), fork(saga$u), fork(saga$v), fork(saga$p), fork(saga$w), fork(saga$x), fork(saga$y), fork(saga$z), fork(saga$A), fork(saga$B), fork(saga$C), fork(saga$D), fork(saga$L), fork(saga$M), fork(saga$N), fork(saga$O)]);
}
function* cartSaga() {
  yield all([fork(saga), fork(saga$1), fork(saga$2), fork(saga$4), fork(saga$9), fork(saga$i), fork(saga$q), fork(saga$s), fork(saga$v), fork(saga$A), fork(saga$N), fork(saga$O)]);
}

const filteredEntitySelector = (selectors, entity, key = 'url') => {
  return [() => [selectors[entity], selector], (entities, config) => {
    if (config && config.activeShop) {
      return keyBy(filter(entities, {
        shop: config.activeShop
      }), key);
    }

    return entities;
  }, PropTypes.object];
};

const reducers = {
  applications: [null, PropTypes.object, reducer],
  cartItems: [null, PropTypes.object, reducer$1],
  carts: [null, PropTypes.object, reducer$2],
  checkoutSettings: [null, PropTypes.object, reducer$3],
  collections: [null, PropTypes.object, reducer$H],
  collectionConditions: [null, PropTypes.object, reducer$I],
  collectionProducts: [null, PropTypes.object, reducer$J],
  contacts: [null, PropTypes.object, reducer$4],
  discounts: [null, PropTypes.object, reducer$B],
  discountProducts: [null, PropTypes.object, reducer$C],
  downloadAccesses: [null, PropTypes.object, reducer$5],
  downloads: [null, PropTypes.object, reducer$6],
  fileUploads: [null, PropTypes.object, reducer$7],
  images: [null, PropTypes.object, reducer$8],
  orders: [null, PropTypes.object, reducer$9],
  orderChartItems: [null, PropTypes.object, reducer$a],
  orderDiscounts: [null, PropTypes.object, reducer$D],
  orderHistoryEvents: [null, PropTypes.object, reducer$b],
  orderItems: [null, PropTypes.object, reducer$c],
  orderOverviews: [null, PropTypes.object, reducer$d],
  orderShippingPrices: [null, PropTypes.object, reducer$d],
  paymentMethods: [null, PropTypes.object, reducer$f],
  pricingCategories: [null, PropTypes.object, reducer$g],
  pricingCategoryPrices: [null, PropTypes.object, reducer$h],
  products: [null, PropTypes.object, reducer$i],
  productAnalytics: [null, PropTypes.object, reducer$K],
  productImages: [null, PropTypes.object, reducer$j],
  productImageManagers: [null, PropTypes.object, reducer$k],
  productVideos: [null, PropTypes.object, reducer$l],
  productVideoManagers: [null, PropTypes.object, reducer$m],
  productQuantities: [null, PropTypes.object, reducer$n],
  productShippings: [null, PropTypes.object, reducer$o],
  productTags: [null, PropTypes.object, reducer$E],
  productTypes: [null, PropTypes.object, reducer$F],
  productVendors: [null, PropTypes.object, reducer$G],
  shippingMethods: [null, PropTypes.object, reducer$p],
  shops: [null, PropTypes.object, reducer$q],
  shopFeeds: [null, PropTypes.object, reducer$r],
  // shopFeedItems: [null, PropTypes.object, shopFeedItems.reducer],
  shopPaymentMethods: [null, PropTypes.object, reducer$s],
  shopRoles: [null, PropTypes.object, reducer$t],
  shopSearches: [null, PropTypes.object, reducer$u],
  shopShippingMethods: [null, PropTypes.object, reducer$v],
  shopVats: [null, PropTypes.object, reducer$w],
  tickets: [null, PropTypes.object, reducer$x],
  ticketPurchases: [null, PropTypes.object, reducer$y],
  ticketPurchaseRegistrations: [null, PropTypes.object, reducer$z],
  users: [null, PropTypes.object, reducer$A],
  videos: [null, PropTypes.object, reducer$L],
  productAccesses: [null, PropTypes.object, reducer$M],
  videoPlayers: [null, PropTypes.object, reducer$N],
  shippingPrices: [null, PropTypes.object, reducer$O]
};
const cartReducers = {
  applications: [null, PropTypes.object, reducer],
  cartItems: [null, PropTypes.object, reducer$1],
  carts: [null, PropTypes.object, reducer$2],
  contacts: [null, PropTypes.object, reducer$4],
  orders: [null, PropTypes.object, reducer$9],
  products: [null, PropTypes.object, reducer$i],
  shops: [null, PropTypes.object, reducer$q],
  shopPaymentMethods: [null, PropTypes.object, reducer$s],
  shopShippingMethods: [null, PropTypes.object, reducer$v],
  users: [null, PropTypes.object, reducer$A],
  videoPlayers: [null, PropTypes.object, reducer$N],
  shippingPrices: [null, PropTypes.object, reducer$O],
  videoAccesses: [null, PropTypes.object, reducer$P]
};
const selectors = ({
  constants,
  selectors
}) => ({
  applications: filteredEntitySelector(selectors, 'applications', 'id'),
  shopPaymentMethods: filteredEntitySelector(selectors, 'shopPaymentMethods'),
  shopShippingMethods: filteredEntitySelector(selectors, 'shopShippingMethods'),
  activeRole: [() => [selectors.shopRoles, selector], (shopRoles, config) => {
    const {
      activeShop
    } = config;

    if (activeShop) {
      const role = find(shopRoles, {
        shop: activeShop
      });

      if (role) {
        return role;
      }
    }

    return null;
  }, PropTypes.object]
});
const cartSelectors = ({
  constants,
  selectors
}) => ({
  activeApplication: [() => [selectors.applications, selector], (applications, config) => {
    const {
      appId
    } = config;
    return find(applications, {
      id: appId
    });
  }, PropTypes.object],
  activeCart: [() => [selectors.carts, selector], (carts, config) => {
    const {
      cartId
    } = config;
    return find(carts, {
      id: cartId
    });
  }, PropTypes.object],
  activeOrder: [() => [selectors.orders, selectors.activeCart], (orders, cart) => {
    if (cart) {
      const {
        url
      } = cart;
      return find(orders, {
        cart: url
      });
    }

    return null;
  }, PropTypes.object],
  activeShop: [() => [selectors.shops, selector], (shops, config) => {
    const {
      shopId
    } = config;
    return find(shops, {
      id: shopId
    });
  }, PropTypes.object]
});

export { applications$1 as Applications, cartItems$1 as CartItems, carts$1 as Carts, checkoutSettings$1 as CheckoutSettings, collectionConditions$1 as CollectionConditions, collectionProducts$1 as CollectionProducts, collections$1 as Collections, contacts$1 as Contacts, discountProducts$1 as DiscountProducts, discounts$1 as Discounts, downloadAccesses$1 as DownloadAccesses, downloads$1 as Downloads, fileUploads$1 as FileUploads, images$1 as Images, orderChartItems$1 as OrderChartItems, orderDiscounts$1 as OrderDiscounts, orderHistoryEvents$1 as OrderHistoryEvents, orderItems$1 as OrderItems, orderOverviews$1 as OrderOverviews, orderShippingPrices$1 as OrderShippingPrices, orders$1 as Orders, paymentMethods$1 as PaymentMethods, pricingCategories$1 as PricingCategories, pricingCategoryPrices$1 as PricingCategoryPrices, productAccesses$1 as ProductAccesses, productAnalytics$1 as ProductAnalytics, productImageManagers$1 as ProductImageManagers, productImages$1 as ProductImages, productQuantities$1 as ProductQuantities, productShippings$1 as ProductShippings, productTags$1 as ProductTags, productTypes$1 as ProductTypes, productVendors$1 as ProductVendors, productVideoManagers$1 as ProductVideoManagers, productVideos$1 as ProductVideos, products$1 as Products, shippingMethods$1 as ShippingMethods, shippingPrices$1 as ShippingPrices, shopFeeds$1 as ShopFeeds, shopPaymentMethods$1 as ShopPaymentMethods, shopRoles$1 as ShopRoles, shopSearches$1 as ShopSearches, shopShippingMethods$1 as ShopShippingMethods, shopVats$1 as ShopVats, shops$1 as Shops, ticketPurchaseRegistrations$1 as TicketPurchaseRegistrations, ticketPurchases$1 as TicketPurchases, tickets$1 as Tickets, users$1 as Users, videoAccesses$1 as VideoAccesses, videoPlayers$1 as VideoPlayers, videos$1 as Videos, cartReducers, cartSaga, cartSelectors, selector as configSelector, getApiClass, reducers, saga$P as saga, selectors, setApiClassSettings };
